import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface ICargoCharacteristicEntityAttributes extends IModelAttributes {
	name: string;
	displayName?: string;
	abbreviation: string;
	iconName?: string;
	showToCustomer?: boolean;
	showToStaff?: boolean;
	appliesToLinkedTrailerVehicle?: boolean;

	isSelectableForVehicle?: boolean;
	isSelectableForTrailer?: boolean;
	selectableLabelTransalationId?: string;

	cargoTypes?: Array<Models.CargoTypeCargoCharacteristic | Models.ICargoTypeCargoCharacteristicAttributes>;

	capacityTypes?: Array<Models.CapacityTypeCargoCharacteristic | Models.ICapacityTypeCargoCharacteristicAttributes>;
}

@entity('CargoCharacteristicEntity', 'Cargo Characteristic')
export default class CargoCharacteristicEntity extends Model implements ICargoCharacteristicEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<CargoCharacteristicEntity, string>()
	public name: string;

	@observable
	@attribute<CargoCharacteristicEntity, string>()
	displayName?: string;

	@observable
	@attribute<CargoCharacteristicEntity, string>()
	abbreviation: string;

	@observable
	@attribute<CargoCharacteristicEntity, boolean>()
	showToCustomer?: boolean;

	@observable
	@attribute<CargoCharacteristicEntity, boolean>()
	showToStaff?: boolean;

	@observable
	@attribute<CargoCharacteristicEntity, boolean>()
	appliesToLinkedTrailerVehicle?: boolean;

	@observable
	@attribute<CargoCharacteristicEntity, boolean>()
	isSelectableForVehicle?: boolean;

	@observable
	@attribute<CargoCharacteristicEntity, boolean>()
	isSelectableForTrailer?: boolean;

	@observable
	@attribute<CargoCharacteristicEntity, string>()
	selectableLabelTransalationId: string;

	@observable
	@attribute<CargoCharacteristicEntity, string>()
	iconName?: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public cargoTypes: Models.CargoTypeCargoCharacteristic[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public capacityTypes: Models.CapacityTypeCargoCharacteristic[] = [];

	constructor(attributes?: Partial<ICargoCharacteristicEntityAttributes>) {
		super(attributes);

		this.cargoTypes = [];
		this.capacityTypes = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ICargoCharacteristicEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.displayName !== undefined) {
				this.displayName = attributes.displayName;
			}
			if (attributes.abbreviation !== undefined) {
				this.abbreviation = attributes.abbreviation;
			}
			if (attributes.iconName !== undefined) {
				this.iconName = attributes.iconName;
			}
			if (attributes.showToCustomer !== undefined) {
				this.showToCustomer = attributes.showToCustomer;
			}
			if (attributes.showToStaff !== undefined) {
				this.showToStaff = attributes.showToStaff;
			}
			if (attributes.appliesToLinkedTrailerVehicle !== undefined) {
				this.appliesToLinkedTrailerVehicle = attributes.appliesToLinkedTrailerVehicle;
			}
			if (attributes.isSelectableForVehicle !== undefined) {
				this.isSelectableForVehicle = attributes.isSelectableForVehicle;
			}
			if (attributes.isSelectableForTrailer !== undefined) {
				this.isSelectableForTrailer = attributes.isSelectableForTrailer;
			}
			if (attributes.selectableLabelTransalationId !== undefined) {
				this.selectableLabelTransalationId = attributes.selectableLabelTransalationId;
			}
			if (attributes.cargoTypes !== undefined && Array.isArray(attributes.cargoTypes)) {
				this.cargoTypes = [];
				for (const model of attributes.cargoTypes) {
					if (model instanceof Models.CargoTypeCargoCharacteristic) {
						this.cargoTypes.push(model);
					} else {
						this.cargoTypes.push(new Models.CargoTypeCargoCharacteristic(model));
					}
				}
			}
			if (attributes.capacityTypes !== undefined && Array.isArray(attributes.capacityTypes)) {
				this.capacityTypes = [];
				for (const model of attributes.capacityTypes) {
					if (model instanceof Models.CapacityTypeCargoCharacteristic) {
						this.capacityTypes.push(model);
					} else {
						this.capacityTypes.push(new Models.CapacityTypeCargoCharacteristic(model));
					}
				}
			}
		}
	}
}

export interface CargoCharacteristicIdentity {
	characteristicId: string;
	name: string;
}
